import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Check if user is a manager (can access admin features)
  const isManager = user?.role === 'Manager';

  // Navigation items organized by category
  const navItems = [
    { path: '/', label: 'Dashboard', icon: '🏠' },
    { path: '/modules', label: 'Module Library', icon: '📖' },
    { path: '/documents', label: 'Documents', icon: '📚' },
    { path: '/progress', label: 'Progress', icon: '📈' },
    { path: '/help', label: 'Help', icon: '❓' },
  ];

  const collaborationItems = [
    { path: '/study-groups', label: 'Study Groups', icon: '👥' },
    { path: '/mentorships', label: 'Mentorships', icon: '🤝' },
  ];

  const adminItems = [
    { path: '/assignments', label: 'Assign Modules', icon: '📋' },
    { path: '/assignment-history', label: 'Assignment History', icon: '📜' },
    { path: '/workload', label: 'PM Workload', icon: '📊' },
    { path: '/workload-settings', label: 'Workload Settings', icon: '⚙️' },
    { path: '/manager-dashboard', label: 'Manager Dashboard', icon: '👑' },
    { path: '/admin', label: 'Admin Panel', icon: '🔧' },
  ];

  const NavLink = ({ item, mobile = false }) => (
    <Link
      to={item.path}
      onClick={mobile ? closeMobileMenu : undefined}
      className={`${mobile ? 'flex items-center px-4 py-3 text-base' : 'px-3 py-2 text-sm'} rounded-md font-medium transition-colors ${
        isActive(item.path)
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {mobile && <span className="mr-3 text-lg">{item.icon}</span>}
      {!mobile && item.icon && <span className="mr-1">{item.icon}</span>}
      {item.label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="bg-blue-600 text-white w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm">
                  PM
                </div>
                <span className="text-lg md:text-xl font-bold text-gray-900 hidden sm:block">Training Hub</span>
              </Link>
            </div>

            {/* Right side - User info and hamburger */}
            <div className="flex items-center space-x-3">
              {user && (
                <span className="hidden sm:block text-sm text-gray-700 truncate max-w-[120px]">
                  {user.username || user.email}
                </span>
              )}

              {/* Logout button - visible on larger screens */}
              <button
                onClick={handleLogout}
                className="hidden sm:block px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Logout
              </button>

              {/* Hamburger menu button - always visible */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50"
          onClick={closeMobileMenu}
        />
      )}

      {/* Menu Drawer */}
      <div className={`fixed top-16 right-0 bottom-0 w-72 bg-white shadow-xl z-30 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="p-4">
          {/* User info in menu */}
          {user && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold">
                    {(user.username || user.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.username || user.email}
                  </p>
                  <p className="text-xs text-gray-500">Logged in</p>
                </div>
              </div>
              <button
                onClick={() => { handleLogout(); closeMobileMenu(); }}
                className="mt-3 w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          )}

          {/* Main Navigation */}
          <div className="mb-4">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Main</p>
            <div className="space-y-1">
              {navItems.map(item => <NavLink key={item.path} item={item} mobile />)}
            </div>
          </div>

          {/* Collaboration */}
          <div className="mb-4">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Collaboration</p>
            <div className="space-y-1">
              {collaborationItems.map(item => <NavLink key={item.path} item={item} mobile />)}
            </div>
          </div>

          {/* Admin - Only visible to managers */}
          {isManager && (
            <div className="mb-4">
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Administration</p>
              <div className="space-y-1">
                {adminItems.map(item => <NavLink key={item.path} item={item} mobile />)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 md:py-8">
        {children}
      </main>
    </div>
  );
}

export default Layout;
